/**
 * Self-service password reset — the request half (Zone 1).
 *
 * Team code + email → `POST /auth/v1/password/reset/request`. The server
 * answers 202 no matter what (anti-enumeration), so the sent state is shown
 * for every submitted address; the copy is honest about that.
 *
 * Arriving from the sign-in page (`?team=slug`) resolves the slug on mount and
 * shows the team confirmation chip instead of the team-code field, mirroring
 * `/login?team=`. A bare `/forgot-password` starts with an empty team code —
 * the last-used team is never prefilled silently.
 *
 * Fan accounts carry no team code — the "I'm a fan" toggle drops the team
 * field and sends a tenant-less request, which the server resolves against
 * the shared fan realm (recorded product decision, grind answer #2).
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { TextField } from '../../components/text-field';
import type { TeamLookup } from '../../lib/api-client';
import { ApiError } from '../../lib/api-error';
import { ROUTES } from '../../lib/constants';

import { validateEmail, validateSlug } from './signup-validation';
import { TeamChip, toTeamLookupMessage } from './team-chip';

export function ForgotPasswordPage(): JSX.Element {
  const apiClient = useApiClient();
  const [searchParams] = useSearchParams();

  const linkedSlug = (searchParams.get('team') ?? '').trim().toLowerCase();
  const [slug, setSlug] = useState(linkedSlug);
  const [team, setTeam] = useState<TeamLookup | null>(null);
  const [isResolving, setIsResolving] = useState(linkedSlug !== '');
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '');
  const [isFanatic, setIsFanatic] = useState(() => searchParams.get('fan') === '1');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [expiresInHours, setExpiresInHours] = useState<number | null>(null);

  // Deep-linked `?team=`: confirm the slug is a real team so the form can show
  // the chip and skip the team-code field. On failure keep the field, with the
  // slug still in it and the error beside it.
  useEffect(() => {
    if (linkedSlug === '') return;
    let isCancelled = false;
    void (async () => {
      const slugError = validateSlug(linkedSlug);
      if (slugError !== null) {
        if (!isCancelled) {
          setFieldErrors({ slug: slugError });
          setIsResolving(false);
        }
        return;
      }
      try {
        const found = await apiClient.lookupTeam(linkedSlug);
        if (isCancelled) return;
        setTeam(found);
        setSlug(found.slug);
      } catch (error) {
        if (isCancelled) return;
        setFieldErrors({ slug: toTeamLookupMessage(error, linkedSlug) });
      } finally {
        if (!isCancelled) setIsResolving(false);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [apiClient, linkedSlug]);

  function onSwitchTeam(): void {
    setTeam(null);
    setSlug('');
    setFieldErrors({});
    setFormError(null);
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const normalised = slug.trim().toLowerCase();
    const errors: Record<string, string> = {};
    if (!isFanatic && team === null) {
      const slugError = validateSlug(normalised);
      if (slugError !== null) errors.slug = slugError;
    }
    const emailError = validateEmail(email, isFanatic ? 'consumer' : 'staff');
    if (emailError !== null) errors.email = emailError;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setFormError(null);
    setIsBusy(true);
    try {
      let result;
      if (isFanatic) {
        // Fan accounts: tenant-less request, resolved server-side.
        result = await apiClient.requestPasswordReset({ email: email.trim() });
      } else {
        // A chip-confirmed team is already resolved; otherwise look the code up.
        const resolved = team ?? (await apiClient.lookupTeam(normalised));
        result = await apiClient.requestPasswordReset({
          tenantId: resolved.tenant_id,
          email: email.trim(),
        });
      }
      setExpiresInHours(result.expires_in_hours);
      setSentTo(email.trim());
    } catch (error) {
      if (ApiError.isApiError(error) && error.status === 404) {
        setFieldErrors({ slug: toTeamLookupMessage(error, normalised) });
      } else {
        setFormError(
          ApiError.isApiError(error) ? error.message : 'Could not send the reset email. Try again.'
        );
      }
    } finally {
      setIsBusy(false);
    }
  }

  const backToLogin =
    team !== null ? `${ROUTES.login}?team=${encodeURIComponent(team.slug)}` : ROUTES.login;

  return (
    <div className="auth">
      <Link to={ROUTES.landing} className="auth__brand">
        <img src="/images/logo-mark.png" alt="" width={32} height={32} />
        <span>
          GRIDIRON <em>TRACKER</em>
        </span>
      </Link>

      <main className="auth__panel">
        {sentTo !== null ? (
          <div className="stack">
            <h1 className="auth__title">Check your email</h1>
            <div className="banner" role="status">
              <span className="banner__title">Reset link sent</span>
              <span>
                If an account exists for <strong>{sentTo}</strong>
                {isFanatic ? '' : ' in this team portal'}, a password reset link is on its way
                {expiresInHours !== null ? ` (valid for ${expiresInHours} hours)` : ''}. The link
                works once.
              </span>
            </div>
            <p className="muted">
              {isFanatic
                ? 'Nothing arrived after a few minutes? Check your spam folder and confirm you typed the email you signed up with.'
                : 'Nothing arrived after a few minutes? Check your spam folder, confirm the team code, or ask your Team Admin to reset your password from User Management.'}
            </p>
            <Link className="btn btn--secondary btn--block" to={backToLogin}>
              Back to sign in
            </Link>
          </div>
        ) : isResolving ? (
          <div className="stack" aria-busy="true">
            <h1 className="auth__title">Reset your password</h1>
            <p className="muted" role="status">
              Finding team <span className="mono">{linkedSlug}</span>…
            </p>
          </div>
        ) : (
          <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
            <div>
              <h1 className="auth__title">Reset your password</h1>
              <p className="muted">
                {isFanatic
                  ? 'Enter the email you sign in with; we’ll email you a single-use reset link.'
                  : team !== null
                    ? 'Enter the email you sign in with; we’ll email you a single-use reset link.'
                    : 'Enter your team code and the email you sign in with; we’ll email you a single-use reset link.'}
              </p>
            </div>
            {!isFanatic && team !== null ? <TeamChip team={team} onSwitch={onSwitchTeam} /> : null}
            <label className="checkbox">
              <input
                checked={isFanatic}
                name="is_fanatic"
                onChange={(event) => setIsFanatic(event.target.checked)}
                type="checkbox"
              />
              <span>I’m a fan account (no team code)</span>
            </label>
            {formError ? (
              <div className="banner banner--error" role="alert">
                <span className="banner__title">Could not send the reset email</span>
                <span>{formError}</span>
              </div>
            ) : null}
            {isFanatic || team !== null ? null : (
              <TextField
                label="Team code"
                name="team_slug"
                required
                autoFocus
                spellCheck={false}
                value={slug}
                error={fieldErrors.slug ?? null}
                onChange={(event) => setSlug(event.target.value.toLowerCase())}
              />
            )}
            <TextField
              label="Email"
              name="email"
              type="email"
              required
              autoFocus={team !== null}
              autoComplete="username"
              value={email}
              error={fieldErrors.email ?? null}
              onChange={(event) => setEmail(event.target.value)}
            />
            <button type="submit" className="btn btn--primary btn--block" disabled={isBusy}>
              {isBusy ? 'Sending…' : 'Email me a reset link'}
            </button>
            <p className="field__hint">
              Remembered it after all? <Link to={backToLogin}>Back to sign in</Link>
            </p>
          </form>
        )}
      </main>
    </div>
  );
}
